import React, { useState, useEffect } from 'react';
import AdminLayout from '../layouts/AdminLayout';
import adminApi from '../api/adminApi';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaTimes } from 'react-icons/fa';

const EMPTY = { name: '', qualification: '', specialty: '', experienceYears: '', bio: '', imageUrl: '', email: '', phone: '', isAvailable: true };

const AdminDoctorsPage = () => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null); // null | 'add' | 'edit'
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [error, setError] = useState('');

  const fetchDoctors = async () => {
    try { const res = await adminApi.get('/doctors/all'); setDoctors(res.data); }
    catch { setError('Failed to load doctors.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchDoctors(); }, []);

  const openAdd = () => { setForm(EMPTY); setEditId(null); setModal('add'); setError(''); };
  const openEdit = doc => {
    setForm({ name: doc.name, qualification: doc.qualification, specialty: doc.specialty, experienceYears: doc.experienceYears, bio: doc.bio, imageUrl: doc.imageUrl || '', email: doc.email, phone: doc.phone, isAvailable: doc.isAvailable });
    setEditId(doc.id); setModal('edit'); setError('');
  };
  const closeModal = () => { setModal(null); setError(''); };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async () => {
    if (!form.name || !form.specialty) { setError('Name and Specialty are required.'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form, experienceYears: parseInt(form.experienceYears) || 0 };
      if (modal === 'add') await adminApi.post('/doctors', payload);
      else await adminApi.put(`/doctors/${editId}`, payload);
      await fetchDoctors();
      closeModal();
    } catch (err) { setError(err.response?.data?.message || 'Failed to save. Please try again.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await adminApi.delete(`/doctors/${deleteId}`);
      setDeleteId(null);
      await fetchDoctors();
    } catch { setError('Failed to delete.'); }
  };

  const filtered = doctors.filter(d =>
    d.name?.toLowerCase().includes(search.toLowerCase()) ||
    d.specialty?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminLayout title="Doctors" subtitle="Manage your specialist doctors">
      <div className="admin-card" id="admin-doctors-card">
        <div className="admin-card-header">
          <div className="admin-card-title">All Doctors ({filtered.length})</div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <div className="admin-search">
              <FaSearch className="admin-search-icon" />
              <input id="doctor-search" placeholder="Search by name or specialty…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <button className="admin-btn admin-btn-primary" onClick={openAdd} id="doctor-add-btn">
              <FaPlus /> Add Doctor
            </button>
          </div>
        </div>

        <div className="admin-table-wrapper">
          <table className="admin-table" id="doctors-table">
            <thead><tr><th>Doctor</th><th>Specialty</th><th>Qualification</th><th>Exp.</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>Loading doctors…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6}><div className="admin-empty"><div className="admin-empty-icon">👨‍⚕️</div><div className="admin-empty-text">No doctors found.</div></div></td></tr>
              ) : filtered.map(doc => (
                <tr key={doc.id} id={`doctor-row-${doc.id}`}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <img src={doc.imageUrl || 'https://images.unsplash.com/photo-1537368910025-700350fe46c7?w=80&h=80&fit=crop'} alt={doc.name} className="admin-table-img" />
                      <div>
                        <div style={{ fontWeight: 700 }}>{doc.name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{doc.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>{doc.specialty}</td>
                  <td style={{ fontSize: '0.8rem' }}>{doc.qualification}</td>
                  <td>{doc.experienceYears}+ yrs</td>
                  <td>
                    <span className={`admin-badge ${doc.isAvailable ? 'active' : 'inactive'}`}>
                      {doc.isAvailable ? '● Active' : '● Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="admin-actions">
                      <button className="admin-btn admin-btn-edit admin-btn-sm" onClick={() => openEdit(doc)} id={`edit-doctor-${doc.id}`}><FaEdit /></button>
                      <button className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => setDeleteId(doc.id)} id={`delete-doctor-${doc.id}`}><FaTrash /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {(modal === 'add' || modal === 'edit') && (
        <div className="admin-modal-overlay" id="doctor-modal-overlay" onClick={closeModal}>
          <div className="admin-modal admin-modal-lg" onClick={e => e.stopPropagation()} id="doctor-modal">
            <div className="admin-modal-header">
              <div className="admin-modal-title">{modal === 'add' ? 'Add New Doctor' : 'Edit Doctor'}</div>
              <button className="admin-modal-close" onClick={closeModal} id="doctor-modal-close">✕</button>
            </div>
            <div className="admin-modal-body">
              {error && <div className="admin-login-error" style={{ marginBottom: '1rem' }}>{error}</div>}
              <div className="admin-form-grid">
                <div className="admin-form-group">
                  <label className="admin-form-label" htmlFor="doc-name">Full Name *</label>
                  <input id="doc-name" name="name" className="admin-form-input" placeholder="Dr. Name" value={form.name} onChange={handleChange} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label" htmlFor="doc-specialty">Specialty *</label>
                  <input id="doc-specialty" name="specialty" className="admin-form-input" placeholder="e.g. GI Surgery" value={form.specialty} onChange={handleChange} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label" htmlFor="doc-qualification">Qualification</label>
                  <input id="doc-qualification" name="qualification" className="admin-form-input" placeholder="MS, MCh…" value={form.qualification} onChange={handleChange} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label" htmlFor="doc-experience">Experience (Years)</label>
                  <input id="doc-experience" name="experienceYears" type="number" className="admin-form-input" placeholder="10" value={form.experienceYears} onChange={handleChange} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label" htmlFor="doc-email">Email</label>
                  <input id="doc-email" name="email" type="email" className="admin-form-input" placeholder="doctor@medicare.com" value={form.email} onChange={handleChange} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label" htmlFor="doc-phone">Phone</label>
                  <input id="doc-phone" name="phone" className="admin-form-input" placeholder="+91-XXXXX-XXXXX" value={form.phone} onChange={handleChange} />
                </div>
                <div className="admin-form-group full">
                  <label className="admin-form-label" htmlFor="doc-imageurl">Photo URL</label>
                  <input id="doc-imageurl" name="imageUrl" className="admin-form-input" placeholder="https://…" value={form.imageUrl} onChange={handleChange} />
                </div>
                <div className="admin-form-group full">
                  <label className="admin-form-label" htmlFor="doc-bio">Bio</label>
                  <textarea id="doc-bio" name="bio" className="admin-form-textarea" rows={4} placeholder="Brief doctor biography…" value={form.bio} onChange={handleChange} />
                </div>
                <div className="admin-form-group full">
                  <label className="admin-toggle" htmlFor="doc-available">
                    <input id="doc-available" type="checkbox" name="isAvailable" checked={form.isAvailable} onChange={handleChange} />
                    <div className="admin-toggle-track"><div className="admin-toggle-thumb" /></div>
                    <span className="admin-form-label" style={{ marginBottom: 0 }}>Available for Appointments</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn-ghost" onClick={closeModal} id="doctor-modal-cancel">Cancel</button>
              <button className="admin-btn admin-btn-primary" onClick={handleSave} disabled={saving} id="doctor-modal-save">
                {saving ? '⏳ Saving…' : modal === 'add' ? '✅ Add Doctor' : '✅ Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="admin-modal-overlay" id="delete-confirm-overlay" onClick={() => setDeleteId(null)}>
          <div className="admin-modal admin-confirm-modal" onClick={e => e.stopPropagation()} id="delete-confirm-modal">
            <div className="admin-modal-body" style={{ textAlign: 'center', padding: '2.5rem 2rem' }}>
              <div className="admin-confirm-icon">🗑️</div>
              <div className="admin-confirm-title">Delete Doctor?</div>
              <p className="admin-confirm-text">This action cannot be undone. The doctor and all related records will be permanently removed.</p>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn-ghost" onClick={() => setDeleteId(null)} id="delete-cancel-btn">Cancel</button>
              <button className="admin-btn admin-btn-danger" onClick={handleDelete} id="delete-confirm-btn">🗑️ Delete</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDoctorsPage;
